import { useState, useMemo } from "react";
import { Panel, Label } from "@/components/SplitLayout";
import CopyButton from "@/components/CopyButton";

const Field = ({ label, ...props }) => (
  <div className="space-y-2">
    <Label>{label}</Label>
    <input
      {...props}
      className="w-full border border-border bg-card px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-primary"
    />
  </div>
);

export default function MetaTagGenerator() {
  const [f, setF] = useState({
    title: "",
    description: "",
    keywords: "",
    author: "",
    url: "",
    image: "",
    twitter: "summary_large_image",
  });

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const esc = (s) =>
    s
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");

  const output = useMemo(() => {
    const lines = [];
    if (f.title) lines.push(`<title>${esc(f.title)}</title>`);
    if (f.description) lines.push(`<meta name="description" content="${esc(f.description)}" />`);
    if (f.keywords) lines.push(`<meta name="keywords" content="${esc(f.keywords)}" />`);
    if (f.author) lines.push(`<meta name="author" content="${esc(f.author)}" />`);
    lines.push(`<meta name="viewport" content="width=device-width, initial-scale=1" />`);
    if (f.title || f.description || f.url || f.image) {
      lines.push("");
      lines.push(`<!-- Open Graph -->`);
      if (f.title) lines.push(`<meta property="og:title" content="${esc(f.title)}" />`);
      if (f.description) lines.push(`<meta property="og:description" content="${esc(f.description)}" />`);
      lines.push(`<meta property="og:type" content="website" />`);
      if (f.url) lines.push(`<meta property="og:url" content="${esc(f.url)}" />`);
      if (f.image) lines.push(`<meta property="og:image" content="${esc(f.image)}" />`);
      lines.push("");
      lines.push(`<!-- Twitter -->`);
      lines.push(`<meta name="twitter:card" content="${f.twitter}" />`);
      if (f.title) lines.push(`<meta name="twitter:title" content="${esc(f.title)}" />`);
      if (f.description) lines.push(`<meta name="twitter:description" content="${esc(f.description)}" />`);
      if (f.image) lines.push(`<meta name="twitter:image" content="${esc(f.image)}" />`);
    }
    return lines.join("\n");
  }, [f]);

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div className="space-y-6">
        <Panel title="Page details">
          <div className="space-y-5">
            <Field label="Page title" value={f.title} onChange={set("title")} placeholder="Best Running Shoes 2026 | Acme" data-testid="meta-title" />
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Description</Label>
                <span className={`font-mono text-xs ${f.description.length > 160 ? "text-accent" : "text-muted-foreground"}`}>
                  {f.description.length}/160
                </span>
              </div>
              <textarea
                data-testid="meta-description"
                value={f.description}
                onChange={set("description")}
                placeholder="A concise, compelling summary of the page…"
                className="h-24 w-full resize-none border border-border bg-card px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <Field label="Keywords (comma separated)" value={f.keywords} onChange={set("keywords")} placeholder="running shoes, marathon, trainers" data-testid="meta-keywords" />
            <Field label="Author" value={f.author} onChange={set("author")} placeholder="Jane Doe" data-testid="meta-author" />
            <Field label="Canonical URL" value={f.url} onChange={set("url")} placeholder="https://acme.com/shoes" data-testid="meta-url" />
            <Field label="Social image URL" value={f.image} onChange={set("image")} placeholder="https://acme.com/og.png" data-testid="meta-image" />
          </div>
        </Panel>
      </div>

      <div className="lg:sticky lg:top-24 lg:self-start">
        <Panel
          title="Generated meta tags"
          testId="meta-output"
          action={output ? <CopyButton text={output} /> : null}
        >
          <pre className="min-h-[300px] overflow-auto whitespace-pre-wrap break-all bg-background p-4 font-mono text-xs leading-relaxed text-foreground">
            {output || <span className="text-muted-foreground">Fill in the fields to generate tags…</span>}
          </pre>
        </Panel>
      </div>
    </div>
  );
}
